<template>
  <div class="search_box">
    <div class="search-menu-trigger" @click="openDialog">
      <Icon icon="ep:search" class="search-icon" />
      <span class="search-text">搜索</span>
    </div>
    <Dialog
      v-model="dialogVisible"
      title="菜单搜索"
      :max-height="600"
      :fullscreen="false"
      append-to-body
      width="600px"
      style="height: 600px"
    >
      <p class="search-tips">点击菜单名称跳转对应页面</p>
      <div class="search-menu-content">
        <el-input
          v-model="searchKeyword"
          placeholder="请输入菜单名称"
          clearable
          size="large"
          @input="handleSearch"
          @keyup.enter="handleSearch"
          ref="searchInputRef"
        >
          <template #prefix>
            <Icon icon="ep:search" />
          </template>
        </el-input>

        <div class="search-results" v-if="searchResults.length > 0">
          <el-scrollbar max-height="400px">
            <div
              v-for="item in searchResults"
              :key="item.id || item.path"
              class="menu-item"
              @click="goToMenu(item)"
            >
              <Icon :icon="item.meta?.icon || 'ep:menu'" class="menu-icon" />
              <div class="menu-info">
                <div class="menu-title">{{ item.displayTitle }}</div>
              </div>
            </div>
          </el-scrollbar>
        </div>

        <div v-else-if="searchKeyword && !searchResults.length" class="no-results">
          <Icon icon="ep:warning" class="no-results-icon" />
          <div>没有找到匹配的菜单</div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Dialog } from '@/components/Dialog'
import { Icon } from '@/components/Icon'
import { usePermissionStore } from '@/store/modules/permission'
import { useI18n } from '@/hooks/web/useI18n'
import { buildSearchableMenus, searchMenus } from './menuSearch'

const router = useRouter()
const permissionStore = usePermissionStore()
const { t } = useI18n()

const dialogVisible = ref(false)
const searchKeyword = ref('')
const searchResults = ref<any[]>([])
const searchInputRef = ref()

// 从用户权限获取侧边菜单数据
const allMenuList = computed(() => {
  const routers = permissionStore.getRouters
  return buildSearchableMenus(routers, t)
})

// 搜索菜单
const handleSearch = () => {
  if (!searchKeyword.value.trim()) {
    searchResults.value = []
    return
  }

  // 只按用户实际看到的中文菜单名称搜索，不再匹配内部路由地址。
  searchResults.value = searchMenus(allMenuList.value, searchKeyword.value)
}

// 跳转到菜单
const goToMenu = (menu: any) => {
  // 使用预先构建好的fullPath
  let targetPath = menu.fullPath

  // 如果是目录类型(catalogue)，跳转到第一个子菜单
  if (menu.type === 'catalogue' || menu.type === 0) {
    if (menu.children && menu.children.length > 0) {
      const firstChild = menu.children[0]
      // 递归查找第一个子菜单的完整路径
      const findFirstLeafPath = (node: any, parentPath: string): string => {
        const currentPath = node.path.startsWith('/') ? node.path : `${parentPath}/${node.path}`
        if (node.children && node.children.length > 0) {
          return findFirstLeafPath(node.children[0], currentPath)
        }
        return currentPath.replace(/\/+/g, '/')
      }
      targetPath = findFirstLeafPath(firstChild, menu.fullPath)
    }
  }

  // 跳转到目标路径
  router.push(targetPath)

  dialogVisible.value = false
  searchKeyword.value = ''
  searchResults.value = []
}

// 打开弹窗
const openDialog = async () => {
  dialogVisible.value = true
  await nextTick()
  searchInputRef.value?.focus()
}

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false
  searchKeyword.value = ''
  searchResults.value = []
}

// 监听ESC键关闭弹窗
onMounted(() => {
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && dialogVisible.value) {
      closeDialog()
    }
  }

  document.addEventListener('keydown', handleEsc)

  // 组件销毁时移除事件监听
  onUnmounted(() => {
    document.removeEventListener('keydown', handleEsc)
  })
})
</script>

<style scoped lang="less">
.search_box {
  background: #f4f4f5;
  border-radius: 20px;
}

.search-menu-trigger {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  cursor: pointer;
  border-radius: 20px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--left-menu-bg-active-color);
  }

  .search-icon {
    margin-right: 8px;
  }

  .search-text {
    font-size: 14px;
    color: var(--el-text-color-regular);
  }
}

.search-tips {
  padding: 5px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #fff;
  background-color: var(--el-color-primary);
}

.search-menu-content {
  .search-results {
    margin-top: 20px;
  }

  .menu-item {
    display: flex;
    padding: 12px 16px;
    margin-bottom: 8px;
    cursor: pointer;
    background-color: aliceblue;
    border-radius: 4px;
    transition: background-color 0.2s;
    align-items: center;

    &:hover {
      background-color: var(--el-color-primary);

      .menu-title {
        color: #fff !important;
      }

      .menu-icon {
        color: #fff !important;
      }
    }

    .menu-icon {
      margin-right: 12px;
      font-size: 18px;
      color: var(--el-text-color-secondary);
    }

    .menu-info {
      flex: 1;

      .menu-title {
        margin-bottom: 4px;
        font-size: 14px;
        font-weight: 500;
        color: var(--el-text-color-primary);
      }

      .menu-path {
        font-size: 12px;
        color: var(--el-text-color-secondary);
      }
    }
  }

  .no-results {
    padding: 40px 0;
    color: var(--el-text-color-secondary);
    text-align: center;

    .no-results-icon {
      margin-bottom: 12px;
      font-size: 24px;
    }
  }
}
</style>
